/* eslint-disable max-lines */
import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  TileFormWrapper,
  TileFormSpan,
  TileFormSection,
  TileFormSectionTitle,
  TileFormRow,
  TileFormColumn,
  DoughnutChart,
  TileFormRowWithData,
  TitleFormSectionSubTitle,
  MapTile,
  LinkWithIconCheckOnMap,
} from '@dataplace.ai/ui-components/atoms'
import { useTranslation } from 'react-i18next'
import { formatNumber } from '@dataplace.ai/functions/utils'
import { AddMapView, ComparedLocationBean, MainDropdown, MapVisualizationModal } from '@dataplace.ai/ui-components/molecules'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'apps/placeme/src/redux/hooks'
import styled, { css } from 'styled-components'
import { AuthContext } from '@dataplace.ai/features'
import { tilesData } from 'apps/placeme/src/features/Analyse/utils/tilesData'
import { useCheckForMobileDevice } from '@dataplace.ai/ui-components/themes'
import { IFeatureCollection } from '@dataplace.ai/ui-components/atoms/MapTile/components/MapOverlays/@types/IFeatureCollection'
import { Loader } from 'libs/shared/ui-components/src/atoms'
import { BasicLayers, GeojsonLayers, WMSLayers, FeatureCollectionLayers } from '@dataplace.ai/ui-components/atoms/MapTile/@types/LayersTypes'
import { IGeojson } from '@dataplace.ai/ui-components/atoms/MapTile/components/MapOverlays/@types/IGeojson'
import useCheckIfPrinting from 'apps/placeme/src/customHooks/useCheckIfPrinting'
import { useTrafficVisMinMaxDates } from '@dataplace.ai/functions/hooks'
import { dataAttributes } from 'apps/placeme/src/constants/constants'
import { IFeatureStyle, IFeatureStyleGroup, ITrafficVisualisationTileData } from './@types/ITrafficVisualisationTileData'
import { RootState } from '../../../../../../../redux/store'
import { fetchWorkspaceUsageValue } from '../../../../../slice/analysisSlice'
import { RangeAndDateTile } from '../../../../molecules'
import { SettingsBox } from '../../../../molecules/SettingsBox'
import { ComparedLocationHeader } from '../../../../atoms'

const TrafficTypeDropdownContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  user-select: none;
`

const StyledTileFormWrapper = styled(TileFormWrapper)<{background: 'light' | 'dark'}>(({
  theme, background,
}) => {
  const { palette } = theme
  return css`
    background-color: ${background === 'light' ? palette.light.white : palette.light.main};
  `
})

interface IExtendedFeatureCollection extends IFeatureCollection {
  data:{
    features:{
      type: string;
      geometry: {
        coordinates: number[];
        type: string;
      };
      properties: {
        title?: string | HTMLElement,
        pinnedItem?: {
          class: string;
          html: string;
        };
        id?: string,
        style: {
          color: string,
          fillColor: string,
          weight: number,
        }
      };
    }[];
  }}

export interface ITrafficTypes {
  total: 'total'
  foot?: 'foot'
  car?: 'car'
}
const allTrafficTypes = {
  total: 'total',
  foot: 'foot',
  car: 'car',
} as const

// eslint-disable-next-line no-restricted-syntax
export const isFeatureStyleGroup = (style: IFeatureStyle | IFeatureStyleGroup): style is IFeatureStyleGroup => 'total' in style || 'foot' in style || 'car' in style

export const useTrafficTypes = (): {
  setTrafficTypes: (trafficTypes: ITrafficTypes) => void,
  selectedTrafficType: keyof typeof trafficTypes,
  TrafficTypeDropdown: JSX.Element | null,
} => {
  const { t } = useTranslation()
  const [trafficTypes, setTrafficTypes] = useState<ITrafficTypes>({
    total: allTrafficTypes.total,
  })
  const [selectedTrafficType, setSelectedTrafficType] = useState<keyof typeof trafficTypes>(trafficTypes.total)

  const trafficTypeOptions = useMemo(() => Object.values(trafficTypes)
    .filter(type => type !== selectedTrafficType)
    .map(type => (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <span
        key={type}
        onClick={() => setSelectedTrafficType(type)}
      >
        {t(`placeme.traffic_visualisation.traffic_type.${type}`)}
      </span>
    )), [selectedTrafficType, trafficTypes])

  const TrafficTypeDropdown = useMemo(() => (Object.keys(trafficTypes).length > 1
    ? (
      <MainDropdown
        content={(
          <TrafficTypeDropdownContent>
            {trafficTypeOptions}
          </TrafficTypeDropdownContent>
        )}
        header={t(`placeme.traffic_visualisation.traffic_type.${selectedTrafficType}`)}
      />
    )
    : null), [trafficTypes])

  return {
    setTrafficTypes,
    selectedTrafficType,
    TrafficTypeDropdown,
  }
}

export const TrafficVisualisationTile: React.FC<{
  data: ITrafficVisualisationTileData, tileId: string, userId: string, isExtraPaid?: boolean}> = ({
  data, tileId, userId, isExtraPaid,
}) => {
  const { t } = useTranslation()
  const { isPrinting } = useCheckIfPrinting()
  const dispatch = useAppDispatch()
  const isMobileDevice = useCheckForMobileDevice()
  const {
    values, comparedLocation,
  } = useSelector((state: RootState) => state.analysis)
  const { value } = useSelector((state: RootState) => state.location)
  const [mapVisible, setMapVisible] = useState(false)
  const [isMapDisplayed, setIsMapDisplayed] = useState(false)
  const [isComparedMapDisplayed, setIsComparedMapDisplayed] = useState(false)
  const {
    minDate, maxDate,
  } = useTrafficVisMinMaxDates()
  const {
    setTrafficTypes,
    selectedTrafficType,
    TrafficTypeDropdown,
  } = useTrafficTypes()

  const [accepted, setAccepted] = useState<boolean>(false)
  const [token, setToken] = useState('')
  const authContext = useContext(AuthContext)

  useEffect(() => {
    if (token.length && !data?.mapLocation) {
      dispatch(fetchWorkspaceUsageValue())
    }
  }, [token, JSON.stringify(data?.mapLocation)])

  useEffect(() => {
    authContext?.userData?.user?.getIdToken()?.then(response => {
      setToken(response)
    })
  }, [authContext])

  const handleAddMap = () => {
    setMapVisible(true)
  }

  const handleMapOpen = (compared: boolean) => {
    if (compared) { setIsComparedMapDisplayed(!isComparedMapDisplayed) }
    else { setIsMapDisplayed(!isMapDisplayed) }
  }

  useEffect(() => {
    if (!data) {
      setAccepted(false)
    }
    if (data?.value) setAccepted(true)
  }, [JSON.stringify(data?.value)])

  const getCatchmentId = values?.find(c => c.id === 'traffic')?.tiles?.find(t =>
    t.id === tileId)?.chosenRange?.catchmentId

  const getLayers = (compared: boolean) => {
    if (data?.value) {
      const items = compared ? data?.value?.comparedLocation : data?.value
      const features : IExtendedFeatureCollection['data']['features'] = []
      const tile = values?.find(c => c.id === 'traffic')?.tiles?.find(t =>
        t.id === tileId)
      const rangeCoords = compared
        ? tile?.comparedChosenRange?.geoJSON?.coordinates
        : tile?.chosenRange?.geoJSON?.coordinates
      items?.map?.features?.forEach((feature, index) => {
        if (isFeatureStyleGroup(feature.properties.style)) {
          if (index === 0) {
            const newTypes: Record<string, string> = {
              total: 'total',
            }
            Object.keys(feature.properties.style).forEach(type => {
              newTypes[type] = type
            })
            setTrafficTypes(newTypes as unknown as ITrafficTypes)
          }
          features.push({
            geometry: feature.geometry,
            properties: {
              ...feature.properties,
              style: feature.properties.style[selectedTrafficType],
            },
            type: 'Feature',
          })
        } else {
          features.push({
            geometry: feature.geometry,
            properties: {
              ...feature.properties,
              style: feature.properties.style,
            },
            type: 'Feature',
          })
        }
      })

      const layers: (BasicLayers | GeojsonLayers | WMSLayers | FeatureCollectionLayers)[] = [
        {
          id: `traffic_layer_${getCatchmentId}`,
          layer: {
            data: {
              features,
              type: 'FeatureCollection',
            },
            options: {
              type: 'geojson',
              id: 'traffic_layer_map',
            },
          },
        }]

      // range layer
      if (rangeCoords) {
        layers.push({
          id: (compared ? `${tile?.id}-compared` : tile?.id) || '',
          layer: {
            data: {
              coordinates: (rangeCoords) as IGeojson['data']['coordinates'],
              type: 'Polygon',
              properties: {},
            },
            options:{
              type: 'geojson',
              id: compared ? 'traffic_range_compared' : 'traffic_range',
              style: {
                color: '#0000a2',
                fillColor:'#0000a2',
                weight: 1.5,
                fillOpacity: 0.05,
              },
            },
          },
        })
      }

      return layers
    }
    return undefined
  }

  const mapLayers = useMemo(() => getLayers(false), [selectedTrafficType, JSON.stringify(data?.value)])
  const comparedMapLayers = useMemo(() => getLayers(true),
    [selectedTrafficType, JSON.stringify(data?.value?.comparedLocation)])

  useEffect(() => {
    if (isPrinting) handleAddMap()
  }, [isPrinting])

  return (
    <StyledTileFormWrapper background={accepted ? 'light' : 'dark'}>

      {(!accepted || (!accepted && data && !data?.loading) || !data)
        ? (
          <RangeAndDateTile
            accepted={accepted}
            category='traffic'
            dateRangeDataCy={dataAttributes.analysePage.selector.dateRange}
            isExtraPaid={isExtraPaid}
            maxDate={maxDate}
            maxRanges={tilesData.find(cat => cat.id === 'traffic')?.tiles.find(tile => tile.id === tileId.split('-')[0])?.maxRanges}
            minDate={minDate}
            rangeTileDataCy={dataAttributes.analysePage.catchment.bar}
            setAccepted={setAccepted}
            tile={tileId}
            userId={userId}
          />
        )
        : (!data || data?.loading
          ? <Loader />
          : (
            <>
              <SettingsBox
                noEdit
                sectionTile='traffic'
                setAccepted={setAccepted}
                tile={tileId}
                typeRanges={{
                  date: {
                    startDate: data?.value?.dataRanges?.startDate,
                    endDate: data?.value?.dataRanges?.endDate,
                  },
                }}
              />
              <TileFormSection>
                <TileFormSectionTitle>{t('placeme.traffic_visualisation_tile.section_title_1')}</TileFormSectionTitle>
                <TileFormRowWithData data-cy={dataAttributes.analysePage.tile.traffic.averageUniqueTraffic}>
                  <span>{t('placeme.traffic_visualisation_tile.row_with_data_1_span_1')}</span>
                  <span>{formatNumber(data?.value?.dailyAverage?.dailyAverageUniqueTraffic)}</span>
                  {data?.value?.comparedLocation?.dailyAverage?.dailyAverageUniqueTraffic && (
                    <ComparedLocationBean
                      comparedAddress={comparedLocation?.location?.address}
                      comparedValue={
                        formatNumber(data?.value?.comparedLocation?.dailyAverage?.dailyAverageUniqueTraffic)
                      }
                      difference={data?.value?.comparedLocation?.dailyAverage?.dailyAverageUniqueTrafficDiff}
                      mainAddress={value?.address}
                      mainValue={formatNumber(data?.value?.dailyAverage?.dailyAverageUniqueTraffic)}
                    />
                  )}
                </TileFormRowWithData>
                <TileFormSpan>
                  {t('placeme.traffic_visualisation_tile.form_span_1')}
                </TileFormSpan>
                <TitleFormSectionSubTitle>{t('placeme.traffic_visualisation_tile.section_sub_title_1')}</TitleFormSectionSubTitle>
                <TileFormRow
                  width='100%'
                  wrap={!isMobileDevice ? 'nowrap' : 'wrap'}
                >
                  <TileFormColumn
                    align='start'
                    width={!isMobileDevice ? '60%' : '100%'}
                  >
                    <TileFormRowWithData legend='#362FB7'>
                      <span>{t('placeme.traffic_visualisation_tile.row_with_data_2_span_1')}</span>
                      <span>{`${parseFloat((data?.value?.visitorsStructure?.stayingInTheArea * 100).toFixed(1))}%`}</span>
                      {data?.value?.comparedLocation?.visitorsStructure?.stayingInTheArea && (
                        <ComparedLocationBean
                          comparedAddress={comparedLocation?.location?.address}
                          comparedValue={`${
                            parseFloat((data?.value?.comparedLocation?.visitorsStructure?.stayingInTheArea * 100).toFixed(1))}%`}
                          difference={data?.value?.comparedLocation?.visitorsStructure?.stayingInTheAreaDiff}
                          mainAddress={value?.address}
                          mainValue={`${parseFloat((data?.value?.visitorsStructure?.stayingInTheArea * 100).toFixed(1))}%`}
                        />
                      )}
                    </TileFormRowWithData>
                    <TileFormSpan>
                      {t('placeme.traffic_visualisation_tile.form_span_2')}
                    </TileFormSpan>
                    <TileFormRowWithData legend='#7E7AD2'>
                      <span>{t('placeme.traffic_visualisation_tile.row_with_data_3_span_1')}</span>
                      <span>{`${parseFloat((data?.value?.visitorsStructure?.passingByTheArea * 100).toFixed(1))}%`}</span>
                      {data?.value?.comparedLocation?.visitorsStructure?.passingByTheArea && (
                        <ComparedLocationBean
                          comparedAddress={comparedLocation?.location?.address}
                          comparedValue={`${parseFloat((data?.value?.comparedLocation?.visitorsStructure?.passingByTheArea * 100).toFixed(1))}%`}
                          difference={data?.value?.comparedLocation?.visitorsStructure?.passingByTheAreaDiff}
                          mainAddress={value?.address}
                          mainValue={`${parseFloat((data?.value?.visitorsStructure?.passingByTheArea * 100).toFixed(1))}%`}
                        />
                      )}
                    </TileFormRowWithData>
                    <TileFormSpan>
                      {t('placeme.traffic_visualisation_tile.form_span_3')}
                    </TileFormSpan>
                  </TileFormColumn>
                  <TileFormColumn width={!isMobileDevice ? '40%' : '100%'}>
                    <DoughnutChart
                      data={[data?.value?.visitorsStructure?.stayingInTheArea * 100,
                        data?.value?.visitorsStructure?.passingByTheArea * 100]
                        .map(number => parseFloat(number.toFixed(1)))}
                      innerLabels
                      labels={[t('placeme.traffic_visualisation_tile.doughnut_chart_1_label_1'),
                        t('placeme.traffic_visualisation_tile.doughnut_chart_1_label_2')]}
                      valueType='percentages'
                      width={230}
                    />
                  </TileFormColumn>
                </TileFormRow>
              </TileFormSection>
              <TileFormSection>
                <TileFormRowWithData>
                  <span>{t('placeme.traffic_visualisation_tile.row_with_data_4_span_1')}</span>
                  <span>{formatNumber(data?.value?.dailyAverage?.averageTrafficPerWeek)}</span>
                  {data?.value?.comparedLocation?.dailyAverage?.averageTrafficPerWeek && (
                    <ComparedLocationBean
                      comparedAddress={comparedLocation?.location?.address}
                      comparedValue={formatNumber(data?.value?.comparedLocation?.dailyAverage?.averageTrafficPerWeek)}
                      difference={data?.value?.comparedLocation?.dailyAverage?.averageTrafficPerWeekDiff}
                      mainAddress={value?.address}
                      mainValue={formatNumber(data?.value?.dailyAverage?.averageTrafficPerWeek)}
                    />
                  )}
                </TileFormRowWithData>
                <TileFormSpan>
                  {t('placeme.traffic_visualisation_tile.form_span_4')}
                </TileFormSpan>
                <TileFormRowWithData>
                  <span>{t('placeme.traffic_visualisation_tile.row_with_data_5_span_1')}</span>
                  <span>{formatNumber(data?.value?.dailyAverage?.averageTrafficPerWeekend)}</span>
                  {data?.value?.comparedLocation?.dailyAverage?.averageTrafficPerWeekend && (
                    <ComparedLocationBean
                      comparedAddress={comparedLocation?.location?.address}
                      // eslint-disable-next-line max-len
                      comparedValue={formatNumber(data?.value?.comparedLocation?.dailyAverage?.averageTrafficPerWeekend)}
                      difference={data?.value?.comparedLocation?.dailyAverage?.averageTrafficPerWeekendDiff}
                      mainAddress={value?.address}
                      mainValue={formatNumber(data?.value?.dailyAverage?.averageTrafficPerWeekend)}
                    />
                  )}
                </TileFormRowWithData>
                <TileFormSpan>
                  {t('placeme.traffic_visualisation_tile.form_span_5')}
                </TileFormSpan>
              </TileFormSection>
              {data?.value?.transportModeStructure && (
                <TileFormSection className='avoid-page-break'>
                  <TitleFormSectionSubTitle>{t('placeme.traffic_visualisation_tile.section_sub_title_2')}</TitleFormSectionSubTitle>
                  <TileFormRow
                    width='100%'
                    wrap={!isMobileDevice ? 'nowrap' : 'wrap'}
                  >
                    <TileFormColumn
                      align='start'
                      width={!isMobileDevice ? '60%' : '100%'}
                    >
                      <TileFormRowWithData legend='#362FB7'>
                        <span>{t('placeme.traffic_visualisation_tile.row_with_data_6_span_1')}</span>
                        <span>{`${parseFloat((data?.value?.transportModeStructure?.onFoot * 100).toFixed(1))}%`}</span>
                        {data?.value?.comparedLocation?.transportModeStructure?.onFoot && (
                          <ComparedLocationBean
                            comparedAddress={comparedLocation?.location?.address}
                            comparedValue={`${parseFloat((data?.value?.comparedLocation?.transportModeStructure?.onFoot * 100).toFixed(1))}%`}
                            difference={data?.value?.comparedLocation?.transportModeStructure?.onFootDiff}
                            mainAddress={value?.address}
                            mainValue={`${parseFloat((data?.value?.transportModeStructure?.onFoot * 100).toFixed(1))}%`}
                          />
                        )}
                      </TileFormRowWithData>
                      <TileFormSpan>
                        {t('placeme.traffic_visualisation_tile.form_span_6')}
                      </TileFormSpan>
                      <TileFormRowWithData legend='#7E7AD2'>
                        <span>{t('placeme.traffic_visualisation_tile.row_with_data_7_span_1')}</span>
                        <span>{`${parseFloat((data?.value?.transportModeStructure?.byCar * 100).toFixed(1))}%`}</span>
                        {data?.value?.comparedLocation?.transportModeStructure?.byCar && (
                          <ComparedLocationBean
                            comparedAddress={comparedLocation?.location?.address}
                            comparedValue={`${parseFloat((data?.value?.comparedLocation?.transportModeStructure?.byCar * 100).toFixed(1))}%`}
                            difference={data?.value?.comparedLocation?.transportModeStructure?.byCarDiff}
                            mainAddress={value?.address}
                            mainValue={`${parseFloat((data?.value?.transportModeStructure?.byCar * 100).toFixed(1))}%`}
                          />
                        )}
                      </TileFormRowWithData>
                      <TileFormSpan>
                        {t('placeme.traffic_visualisation_tile.form_span_7')}
                      </TileFormSpan>
                    </TileFormColumn>
                    <TileFormColumn width={!isMobileDevice ? '40%' : '100%'}>
                      <DoughnutChart
                        data={[data?.value?.transportModeStructure?.onFoot * 100,
                          data?.value?.transportModeStructure?.byCar * 100]
                          .map(number => parseFloat(number.toFixed(1)))}
                        innerLabels
                        labels={[t('placeme.traffic_visualisation_tile.doughnut_chart_2_label_1'),
                          t('placeme.traffic_visualisation_tile.doughnut_chart_2_label_2')]}
                        valueType='percentages'
                        width={230}
                      />
                    </TileFormColumn>
                  </TileFormRow>
                  <TileFormRowWithData>
                    <span>{t('placeme.traffic_visualisation_tile.row_with_data_8_span_1')}</span>
                    <span>{formatNumber(data?.value?.transportModeStructure?.averageNumberOfCarPassingByTheArea)}</span>
                    {data?.value?.comparedLocation?.transportModeStructure?.averageNumberOfCarPassingByTheArea && (
                      <ComparedLocationBean
                        comparedAddress={comparedLocation?.location?.address}
                        comparedValue={formatNumber(
                          data?.value?.comparedLocation?.transportModeStructure?.averageNumberOfCarPassingByTheArea,
                        )}
                        difference={
                          data?.value?.comparedLocation?.transportModeStructure?.averageNumberOfCarPassingByTheAreaDiff
                        }
                        mainAddress={value?.address}
                        mainValue={formatNumber(
                          data?.value?.transportModeStructure?.averageNumberOfCarPassingByTheArea,
                        )}
                      />
                    )}
                  </TileFormRowWithData>
                  <TileFormSpan>
                    {t('placeme.traffic_visualisation_tile.form_span_8')}
                  </TileFormSpan>
                </TileFormSection>
              )}
              <TileFormSection>
                {(!mapVisible) && (
                  <AddMapView
                    buttonValue={1}
                    dataCy={dataAttributes.analysePage.tile.traffic.addMapViewWrapper}
                    description='placeme.add_traffic_visualisation_map.description'
                    onChange={handleAddMap}
                    title='placeme.add_traffic_visualisation_map.title'
                  />
                )}
                {mapVisible && (
                  <>
                    {data?.value?.comparedLocation
                    && (
                      <ComparedLocationHeader
                        style={{
                          padding: '0',
                        }}
                      >
                        <h5>{t('placeme.municipality_population.compared_location.header_1')}</h5>
                        {' '}
                        <span>{value?.address}</span>
                      </ComparedLocationHeader>
                    )}
                    <TitleFormSectionSubTitle>
                      <span>{t('placeme.traffic_visualisation_tile.section_sub_title_2_span_1')}</span>
                      {TrafficTypeDropdown}
                      <LinkWithIconCheckOnMap onClick={() => handleMapOpen(false)} />
                    </TitleFormSectionSubTitle>
                    <MapTile
                      dragging
                      height='300px'
                      layers={mapLayers}
                      location={value}
                      mapId='example-map-data-tile'
                      pinDisplayed
                      popupHeading={`${t('generic.chosen_location')}:`}
                      popupParagraph={value?.address}
                      showScaleControl
                      width='100%'
                      zoom={15}
                      zoomControl
                    />
                    {isMapDisplayed && (
                      <MapVisualizationModal
                        isDisplayed={isMapDisplayed}
                        layers={mapLayers}
                        location={value}
                        mapId={`traffic-visualisation-tile-${values?.find(c => c.id === 'traffic')?.tiles?.find(t => t.id === 'traffic_visualization')?.chosenRange?.catchmentId}`}
                        setIsDisplay={setIsMapDisplayed}
                        trafficTypeDropdown={TrafficTypeDropdown}
                        zoom={15}
                      />
                    )}
                    {data?.value?.comparedLocation?.map
                    && (
                      <>
                        <ComparedLocationHeader second>
                          <h5>{t('placeme.municipality_population.compared_location.header_2')}</h5>
                          {' '}
                          <span>{comparedLocation?.location?.address}</span>
                        </ComparedLocationHeader>
                        <TitleFormSectionSubTitle>
                          <span>{t('placeme.traffic_visualisation_tile.section_sub_title_2_span_1')}</span>
                          {TrafficTypeDropdown}
                          <LinkWithIconCheckOnMap onClick={() => handleMapOpen(true)} />
                        </TitleFormSectionSubTitle>
                        <MapTile
                          dragging
                          height='300px'
                          layers={comparedMapLayers}
                          location={comparedLocation?.location}
                          mapId='example-map-data-tile'
                          pinDisplayed
                          popupHeading={`${t('generic.chosen_location')}:`}
                          popupParagraph={comparedLocation?.location?.address}
                          showScaleControl
                          width='100%'
                          zoom={15}
                          zoomControl
                        />
                      </>
                    )}
                  </>
                )}

                {isComparedMapDisplayed && data?.value?.comparedLocation?.map && (
                  <MapVisualizationModal
                    isDisplayed={isComparedMapDisplayed}
                    layers={comparedMapLayers}
                    location={comparedLocation?.location}
                    mapId={`traffic-visualisation-tile-${values?.find(c => c.id === 'traffic')?.tiles?.find(t => t.id === 'traffic_visualization')?.chosenRange?.catchmentId}_compared`}
                    setIsDisplay={setIsComparedMapDisplayed}
                    trafficTypeDropdown={TrafficTypeDropdown}
                    zoom={15}
                  />
                )}

              </TileFormSection>
            </>
          ))}

    </StyledTileFormWrapper>
  )
}

export default TrafficVisualisationTile
